import type { Forderungsakte } from '@/hooks/useForderungen';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CheckCircle } from 'lucide-react';
import {
  pruefePlausibilitaet,
  AMPEL_FARBEN,
  AMPEL_LABELS,
  type PruefErgebnis,
  type PruefStatus,
} from '@/lib/plausibilitaet';

const AMPEL_CONFIG: { key: keyof ReturnType<typeof pruefePlausibilitaet>; label: string }[] = [
  { key: 'schadentag', label: 'Schadentag ↔ Mietbeginn' },
  { key: 'abrechnung', label: 'Mietende ↔ Rechnungsdatum' },
  { key: 'mietdauer', label: 'Mietdauer ↔ Reparaturdauer' },
  { key: 'verjaehrung', label: 'Verjährung' },
];

function AmpelPunkt({ status }: { status: PruefStatus }) {
  return (
    <span
      data-testid={`plausibilitaet-ampel-${status}`}
      className={`inline-block w-3 h-3 rounded-full shrink-0 ${AMPEL_FARBEN[status]}`}
      aria-label={AMPEL_LABELS[status]}
    />
  );
}

function AmpelZeile({ label, ergebnis }: { label: string; ergebnis: PruefErgebnis }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`plausibilitaet-trigger-${label}`}
          className="flex items-center justify-between gap-3 w-full text-left py-1.5 px-2 -mx-2 rounded hover:bg-muted/50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
        >
          <span className="flex items-center gap-2 min-w-0">
            <AmpelPunkt status={ergebnis.status} />
            <span className="text-sm font-medium truncate">{label}</span>
          </span>
          <span className="text-xs text-muted-foreground shrink-0">{ergebnis.wert}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="text-sm max-w-xs">
        <div className="flex items-center gap-2 mb-2">
          <AmpelPunkt status={ergebnis.status} />
          <span className="font-semibold">
            {label} — {AMPEL_LABELS[ergebnis.status]}
          </span>
        </div>
        <p className="text-muted-foreground leading-relaxed">{ergebnis.erklaerung}</p>
      </PopoverContent>
    </Popover>
  );
}

export default function PlausibilitaetsBlock({ forderung }: { forderung: Forderungsakte }) {
  const ergebnis = pruefePlausibilitaet({
    unfall_datum: forderung.unfall_datum,
    mietbeginn: forderung.mietbeginn,
    mietende: forderung.mietende,
    mietdauer_tage: forderung.mietdauer_tage,
    reparaturdauer_voraussichtlich: forderung.reparaturdauer_voraussichtlich,
    reparaturdauer_tatsaechlich: forderung.reparaturdauer_tatsaechlich,
    rechnung_datum: forderung.rechnung_datum,
    verjährungsfrist: forderung.verjährungsfrist,
  });

  return (
    <Card data-testid="plausibilitaets-block">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm">Plausibilität</h3>
        </div>
        <div className="divide-y">
          {AMPEL_CONFIG.map(({ key, label }) => (
            <AmpelZeile key={key} label={label} ergebnis={ergebnis[key]} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
